import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { useForm } from '@tanstack/react-form';
import { ButtonType } from '@src/types/types';
import { Button } from '@components/atoms/Button';
import Input from '@components/atoms/Input';
import { apiClient } from '@src/api/client';
import { ExternalLinks, Paths } from '@src/routes/paths';
import { Card } from '@components/atoms/Card';

export const RegisterSampleView = () => {
  const { t } = useTranslation('view', { keyPrefix: 'registerSample' });
  const { t: tError } = useTranslation('view', { keyPrefix: 'error' });
  const { t: tLegacy } = useTranslation('legacy');
  const navigate = useNavigate();
  const auth = useAuth();

  const { mutateAsync } = apiClient.useMutation('post', '/api/sample/verify');

  const defaultValues: { code: string | undefined } = {
    code: undefined,
  };

  const form = useForm({
    defaultValues: defaultValues,
    validators: {
      onSubmitAsync: async ({ value }) => {
        const code = value.code;
        if (!code) {
          return {
            fields: {
              code: tError('empty'),
            },
          };
        }
        try {
          const result = await mutateAsync({ body: { code } });
          if (result.status === 'VALID') {
            if (auth.user) {
              navigate(`${Paths.CLAIM_SAMPLE}?claimCode=${code}`);
            } else {
              navigate(
                `${Paths.LOGIN}?returnTo=${Paths.CLAIM_SAMPLE}?claimCode=${code}`,
              );
            }
          } else if (result.status === 'CANCELLED') {
            return {
              fields: {
                code: t('cancelledCode'),
              },
            };
          } else {
            return {
              fields: {
                code: t('invalidCode'),
              },
            };
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error: unknown) {
          return {
            fields: {
              code: tError('connectionError'),
            },
          };
        }
        return null;
      },
    },
  });

  return (
    <div className="flex items-center justify-center py-4 sm:py-10">
      <Card className="w-full max-w-[480px] px-5 py-6 sm:px-11 sm:py-12">
        <h1 className="text-2xl font-bold text-dark mb-2">{t('title')}</h1>
        <p className="text-sm text-[#555] leading-relaxed mb-8">
          {t('subtitle')}
        </p>

        <div className="bg-ivarioYellow/10 border-l-[3px] border-ivarioYellow rounded-lg p-4 mb-7">
          <strong className="block text-sm font-semibold text-dark mb-0.5">
            {t('hintTitle')}
          </strong>
          <span className="text-sm text-[#555] leading-relaxed">
            {t('hintDescription')}
          </span>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field
            name="code"
            validators={{
              onBlur: ({ value }) => (!value ? tError('empty') : undefined),
            }}
            children={(field) => (
              <Input
                label={t('code')}
                placeholder={t('codePlaceholder')}
                containerClassName="[&>label]:uppercase [&>label]:text-sm [&>label]:tracking-wide"
                value={field.state.value ?? ''}
                handleChange={(e) => field.handleChange(e.target.value)}
                type="text"
                name={field.name}
                id={field.name}
                testId="code"
                handleBlur={field.handleBlur}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
              />
            )}
          />
          <p className="text-xs text-[#555] mt-1.5 mb-6">{t('inputNote')}</p>

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                buttonType={ButtonType.PRIMARY}
                label={t('verify')}
                testingId="verifyCode"
                type="submit"
                disabled={!canSubmit}
                loading={isSubmitting}
                className="w-full"
              />
            )}
          />
        </form>

        <hr className="border-t border-grey3light my-7" />

        <p className="text-sm text-[#555] text-center">
          {tLegacy('register')}
          <a
            href={ExternalLinks.LEGACY_LOGIN}
            className="underline text-dark font-medium"
          >
            {tLegacy('registerLink')}
          </a>
        </p>
      </Card>
    </div>
  );
};
